/**
 * DataBest — Global API client (axios) with interceptors.
 * Reads REACT_APP_BACKEND_URL from environment (never hardcoded).
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
    // Surface misconfiguration loudly during development.
    console.error(
        "REACT_APP_BACKEND_URL is not defined. API calls will fail.",
    );
}

export const API_BASE = `${BACKEND_URL}/api`;
const STORAGE_KEY = "databest.auth";

const api = axios.create({
    baseURL: API_BASE,
    timeout: 30000,
    withCredentials: false,
});

// ---------------------------------------------------------------
// Storage helpers (read/write the persisted Zustand auth slice)
// ---------------------------------------------------------------
const readAuth = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (_) {
        return null;
    }
};

const writeTokens = (accessToken, refreshToken) => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        parsed.state = {
            ...(parsed.state || {}),
            accessToken,
            refreshToken,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    } catch (_) {
        /* ignore */
    }
};

const clearAuthAndRedirect = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (_) {
        /* ignore */
    }
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.href = "/login";
    }
};

// ============================================================
// Request interceptor — attach JWT (populated by authStore)
// ============================================================
api.interceptors.request.use(
    (config) => {
        const token = readAuth()?.state?.accessToken;
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (err) => Promise.reject(err),
);

// ============================================================
// Response interceptor — silent refresh on 401 "Token expired"
// ============================================================
let refreshPromise = null; // Single in-flight refresh shared by all callers.

const isExpiredError = (err) =>
    err?.response?.status === 401 &&
    err?.response?.data?.detail === "Token expired";

const extractMessage = (detail, fallback) => {
    if (!detail) return fallback;
    if (typeof detail === "string") return detail;
    if (typeof detail === "object") {
        // Structured 400 payload: { field, message, hint }
        if (detail.message) {
            return detail.hint
                ? `${detail.message} — ${detail.hint}`
                : detail.message;
        }
        // Pydantic 422: [{loc, msg, type}, ...]
        if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
    }
    return fallback;
};

const normalise = (err) => {
    const msg = extractMessage(
        err?.response?.data?.detail,
        err?.response?.data?.message || err?.message || "Network error",
    );
    // Return a proper Error so `.message` works and unhandled rejections
    // stringify to something readable (avoids "[object Object]").
    const e = new Error(msg);
    e.status = err?.response?.status ?? 0;
    e.data = err?.response?.data ?? null;
    return e;
};

api.interceptors.response.use(
    (res) => res,
    async (err) => {
        const original = err?.config;

        // Only try to refresh once per request and never for the refresh call itself.
        if (
            isExpiredError(err) &&
            original &&
            !original._retry &&
            !original.url?.includes("/auth/refresh")
        ) {
            original._retry = true;

            const refreshToken = readAuth()?.state?.refreshToken;
            if (!refreshToken) {
                clearAuthAndRedirect();
                return Promise.reject(normalise(err));
            }

            try {
                // Share a single in-flight refresh across concurrent 401s.
                if (!refreshPromise) {
                    refreshPromise = axios
                        .post(`${API_BASE}/auth/refresh`, {
                            refresh_token: refreshToken,
                        })
                        .then((r) => {
                            writeTokens(r.data.access_token, r.data.refresh_token);
                            return r.data.access_token;
                        })
                        .finally(() => {
                            // Reset regardless of success/failure so future 401s can retry.
                            const p = refreshPromise;
                            // Defer reset by a microtask so all waiters get the same result.
                            Promise.resolve().then(() => {
                                if (refreshPromise === p) refreshPromise = null;
                            });
                        });
                }

                const newAccessToken = await refreshPromise;
                original.headers = original.headers || {};
                original.headers.Authorization = `Bearer ${newAccessToken}`;
                return api(original);
            } catch (refreshErr) {
                // Refresh token itself expired or invalid → force re-login.
                clearAuthAndRedirect();
                return Promise.reject(normalise(refreshErr));
            }
        }

        return Promise.reject(normalise(err));
    },
);

export default api;
